import React, { useEffect } from 'react';
import { validateRequiredParameters } from '../utils/parameterEncryption';
import { useEncryptedParams } from '../contexts/EncryptedParamsContext';

interface ParameterValidatorProps {
  searchParams: URLSearchParams;
  children: React.ReactNode;
  onValidationError?: (validation: any) => void;
}

/**
 * Componente que valida los parámetros requeridos antes de mostrar el viewer
 */
const ParameterValidator: React.FC<ParameterValidatorProps> = ({
  searchParams,
  children,
  onValidationError
}) => {
  const { setSearchParams, params, error, errorTitle, isLoading } = useEncryptedParams();

  // Inicializar los parámetros en el contexto cuando el componente se monta
  useEffect(() => {
    if (searchParams) {
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  // Mientras se valida el enlace contra el backend, no se muestra el visor.
  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-black">
        <div className="text-center text-gray-300">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-2 border-gray-600 border-t-white" />
          <p className="text-sm">Validando el enlace…</p>
        </div>
      </div>
    );
  }

  // Validar que todos los parámetros requeridos estén presentes
  const validation = validateRequiredParameters(searchParams);

  // Si ya hay params validados, el token pudo haberse limpiado de la URL: se
  // deja pasar. La validación solo bloquea ANTES de tener params.
  if (!validation.isValid && !params) {
    const errorMessage = validation.message; // Ya no incluye parámetros técnicos

    // Llamar al callback de error si se proporciona
    if (onValidationError) {
      onValidationError(validation);
    }

    return (
      <div className="flex h-screen w-full items-center justify-center bg-black">
        <div className="text-center text-white">
          <div className="mb-4">
            <svg
              className="mx-auto h-16 w-16 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="mb-2 text-xl font-bold text-red-500">Acceso No Autorizado</h2>
          <p className="mb-4 text-gray-300">{errorMessage}</p>
          <p className="text-sm text-gray-500">
            Por favor, contacte al administrador del sistema.
          </p>
        </div>
      </div>
    );
  }

  // Si hay error en la desencriptación/validación (token inválido, expirado, revocado...)
  if (error || !params) {
    const errorMessage = error || "Los parámetros de seguridad no son válidos o han sido alterados.";
    const tituloError = errorTitle || 'Parámetros de Seguridad Inválidos';

    if (onValidationError) {
      onValidationError({
        isValid: false,
        message: errorMessage,
        type: 'decryption_error'
      });
    }

    return (
      <div className="flex h-screen w-full items-center justify-center bg-black">
        <div className="text-center text-white">
          <div className="mb-4">
            <svg
              className="mx-auto h-16 w-16 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 0h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h2 className="mb-2 text-xl font-bold text-red-500">{tituloError}</h2>
          <p className="mb-4 text-gray-300">{errorMessage}</p>
          <p className="text-sm text-gray-500">
            Los parámetros de acceso no son válidos. Por favor, utilice un enlace autorizado.
          </p>
        </div>
      </div>
    );
  }

  // Si llegamos aquí, los parámetros son válidos, renderizar los children
  return <>{children}</>;
};

export default ParameterValidator;
